import classnames from 'classnames';
import DOMPurify from 'dompurify';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';

import ReactQuill from 'react-quill';
import styles from './ReadMoreLessHtml.module.scss';

interface IProps {
  html: string;
  lines?: number;
}

const ReadMoreLessHtml = ({ html, lines = 2 }: IProps): ReactElement => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const [showFull, setShowFull] = useState(false);
  const [isOverflowed, setIsOverflowed] = useState(false);

  const safeHtml = useMemo(() => DOMPurify.sanitize(html ?? ''), [html]);

  useEffect(() => {
    let raf = 0;
    let ro: ResizeObserver | null = null;

    const wrapper = wrapperRef.current;
    if (wrapper) {
      const editorEl = wrapper.querySelector('.ql-editor') as HTMLDivElement | null;

      if (editorEl) {
        let last: boolean | null = null;

        const check = () => {
          const next = editorEl.scrollHeight > editorEl.clientHeight + 1;
          if (last === next) return;
          last = next;
          setIsOverflowed(next);
        };

        check();
        raf = requestAnimationFrame(check);

        ro = new ResizeObserver(check);
        ro.observe(editorEl);
      }
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro?.disconnect();
    };
  }, [safeHtml, lines]);

  return (
    <div
      ref={wrapperRef}
      className={classnames(styles.contentContainer, { [styles.full]: showFull })}
      style={!showFull ? ({ ['--clamp-lines' as any]: lines } as React.CSSProperties) : undefined}
    >
      <ReactQuill value={safeHtml} readOnly theme="bubble" modules={{ toolbar: false }} />

      {(isOverflowed || showFull) && (
        <div
          className={classnames(styles.readMoreLessContainer, {
            [styles.full]: showFull,
          })}
        >
          <span
            role="presentation"
            className={styles.readMoreLessToggle}
            onClick={() => setShowFull(prev => !prev)}
          >
            {showFull ? 'Read Less' : 'Read More'}
          </span>
        </div>
      )}
    </div>
  );
};

export default ReadMoreLessHtml;
